import { createElement } from 'react'
import { createRoute, redirect, type RootRoute } from '@tanstack/react-router'

import Home from '@/Home'
import { BookingsScreenContainer } from '@/screens/BookingsScreenContainer'
import { CalloutLogWorkScreenContainer } from '@/screens/CalloutLogWorkScreenContainer'
import { CalloutDetailsScreenContainer } from '@/screens/CalloutDetailsScreenContainer'
import { CustomersScreenContainer } from '@/screens/CustomersScreenContainer'
import {
  CustomerDetailsScreenContainer,
  CustomerDocumentsScreenContainer,
  CustomerHistoryScreenContainer,
  CustomerNotesScreenContainer,
  CustomerServicesScreenContainer,
} from '@/screens/CustomerDetailsScreenContainer'
import { FollowUpsScreenContainer } from '@/screens/FollowUpsScreenContainer'
import { InvoiceServiceScreenContainer } from '@/screens/InvoiceServiceScreenContainer'
import { ArchivedServicesScreenContainer } from '@/screens/ArchivedServicesScreen'
import { LoginScreenContainer } from '@/screens/LoginScreenContainer'
import { ProfileScreenContainer } from '@/screens/ProfileScreenContainer'
import { AboutScreenContainer } from '@/screens/AboutScreenContainer'
import { HelpScreenContainer } from '@/screens/HelpScreenContainer'
import { HelpCalloutStatusChangesScreenContainer } from '@/screens/HelpCalloutStatusChangesScreenContainer'
import { ServicesScreenContainer } from '@/screens/ServicesScreenContainer'
import { RemindersPage } from '@/screens/RemindersPage'
import { SettingsScreenContainer } from '@/screens/SettingsScreenContainer'
import { SessionDetailsScreenContainer } from '@/screens/SessionDetailsScreenContainer'
import { SessionLogWorkScreenContainer } from '@/screens/SessionLogWorkScreenContainer'
import { SessionInvoiceServiceScreenContainer } from '@/screens/SessionInvoiceServiceScreenContainer'
import { JobDetailsScreenContainer } from '@/screens/JobDetailsScreenContainer'
import { JobLogWorkScreenContainer } from '@/screens/JobLogWorkScreenContainer'
import { JobInvoicePrepScreenContainer } from '@/screens/JobInvoicePrepScreenContainer'
import { KioskScreenContainer, KioskTabletScreenContainer } from '@/screens/kiosk'
import { KioskPairingApproveScreen } from '@/screens/KioskPairingApproveScreen'

export default (parentRoute: RootRoute) => {
  const indexRoute = createRoute({
    getParentRoute: () => parentRoute,
    path: '/',
    component: Home,
  })

  const bookingsRoute = createRoute({
    path: '/bookings',
    component: BookingsScreenContainer,
    getParentRoute: () => parentRoute,
  })

  const customersRoute = createRoute({
    path: '/customers',
    component: CustomersScreenContainer,
    getParentRoute: () => parentRoute,
  })

  const customerDetailsRoute = createRoute({
    path: '/customers/$id',
    component: CustomerDetailsScreenContainer,
    getParentRoute: () => parentRoute,
  })

  const customerServicesRoute = createRoute({
    path: '/customers/$id/services',
    component: CustomerServicesScreenContainer,
    getParentRoute: () => parentRoute,
  })

  const customerNotesRoute = createRoute({
    path: '/customers/$id/notes',
    component: CustomerNotesScreenContainer,
    getParentRoute: () => parentRoute,
    // Deep link to a thread: ?thread=fu:<id> | svc:<Type>:<id> | general.
    validateSearch: (search: Record<string, unknown>) => ({
      thread: typeof search.thread === 'string' ? search.thread : undefined,
    }),
  })

  const customerHistoryRoute = createRoute({
    path: '/customers/$id/history',
    component: CustomerHistoryScreenContainer,
    getParentRoute: () => parentRoute,
  })

  const customerDocumentsRoute = createRoute({
    path: '/customers/$id/documents',
    component: CustomerDocumentsScreenContainer,
    getParentRoute: () => parentRoute,
  })

  const followUpsRoute = createRoute({
    path: '/follow-ups',
    component: FollowUpsScreenContainer,
    getParentRoute: () => parentRoute,
    // ?tab=upcoming|archive — the default (absent) tab is Due now.
    // ?new=1 opens the New follow-up dialog on arrival (header menu link).
    validateSearch: (search: Record<string, unknown>) => ({
      tab:
        search.tab === 'upcoming'
          ? ('upcoming' as const)
          : search.tab === 'archive'
            ? ('archive' as const)
            : undefined,
      new: search.new ? true : undefined,
    }),
  })

  const servicesRoute = createRoute({
    path: '/services',
    component: ServicesScreenContainer,
    getParentRoute: () => parentRoute,
  })

  const archivedRoute = createRoute({
    path: '/archived',
    component: ArchivedServicesScreenContainer,
    getParentRoute: () => parentRoute,
  })

  // The per-type ledgers were folded into the unified /services page.
  const calloutsRoute = createRoute({
    path: '/callouts',
    getParentRoute: () => parentRoute,
    beforeLoad: () => {
      throw redirect({ to: '/services' })
    },
  })

  const sessionsRoute = createRoute({
    path: '/sessions',
    getParentRoute: () => parentRoute,
    beforeLoad: () => {
      throw redirect({ to: '/services' })
    },
  })

  const legacySessionsRoute = createRoute({
    path: '/remotesessions',
    getParentRoute: () => parentRoute,
    beforeLoad: () => {
      throw redirect({ to: '/sessions' })
    },
  })

  const sessionDetailsRoute = createRoute({
    path: '/sessions/$id',
    component: SessionDetailsScreenContainer,
    getParentRoute: () => parentRoute,
  })

  const legacySessionDetailsRoute = createRoute({
    path: '/remotesessions/$id',
    getParentRoute: () => parentRoute,
    beforeLoad: ({ params }) => {
      throw redirect({ to: '/sessions/$id', params: { id: params.id } })
    },
  })

  const calloutDetailsRoute = createRoute({
    path: '/callouts/$id',
    component: CalloutDetailsScreenContainer,
    getParentRoute: () => parentRoute,
  })

  const calloutLogWorkRoute = createRoute({
    path: '/callouts/$id/logs',
    component: CalloutLogWorkScreenContainer,
    getParentRoute: () => parentRoute,
  })

  const calloutInvoiceRoute = createRoute({
    path: '/callouts/$id/invoice',
    component: InvoiceServiceScreenContainer,
    getParentRoute: () => parentRoute,
  })

  const sessionLogWorkRoute = createRoute({
    path: '/sessions/$id/logs',
    component: SessionLogWorkScreenContainer,
    getParentRoute: () => parentRoute,
  })

  const legacySessionLogWorkRoute = createRoute({
    path: '/remotesessions/$id/logs',
    getParentRoute: () => parentRoute,
    beforeLoad: ({ params }) => {
      throw redirect({ to: '/sessions/$id/logs', params: { id: params.id } })
    },
  })

  const sessionInvoiceRoute = createRoute({
    path: '/sessions/$id/invoice',
    component: SessionInvoiceServiceScreenContainer,
    getParentRoute: () => parentRoute,
  })

  const legacySessionInvoiceRoute = createRoute({
    path: '/remotesessions/$id/invoice',
    getParentRoute: () => parentRoute,
    beforeLoad: ({ params }) => {
      throw redirect({ to: '/sessions/$id/invoice', params: { id: params.id } })
    },
  })

  // The Reminders page: one URL per section; /reminders/<id> is a single reminder.
  const remindersRoute = createRoute({
    path: '/reminders',
    component: () => createElement(RemindersPage, { tab: 'scheduled' }),
    getParentRoute: () => parentRoute,
  })
  const remindersArchivedRoute = createRoute({
    path: '/reminders/archived',
    component: () => createElement(RemindersPage, { tab: 'archived' }),
    getParentRoute: () => parentRoute,
  })
  const remindersMessagesRoute = createRoute({
    path: '/reminders/messages',
    component: () => createElement(RemindersPage, { tab: 'messages' }),
    getParentRoute: () => parentRoute,
  })
  const remindersConfigureRoute = createRoute({
    path: '/reminders/configure',
    component: () => createElement(RemindersPage, { tab: 'configure' }),
    getParentRoute: () => parentRoute,
  })
  const reminderDetailRoute = createRoute({
    path: '/reminders/$reminderId',
    component: function ReminderDetailRoute() {
      const { reminderId } = reminderDetailRoute.useParams()
      return createElement(RemindersPage, { tab: 'reminder', reminderId })
    },
    getParentRoute: () => parentRoute,
  })

  // WhatsApp used to be its own page; keep old links (incl. ?reminder=<id>) working.
  const whatsappRoute = createRoute({
    path: '/whatsapp',
    beforeLoad: () => {
      const params = new URLSearchParams(window.location.search)
      const reminder = params.get('reminder')
      throw redirect({ href: reminder ? `/reminders/${encodeURIComponent(reminder)}` : '/reminders/messages' })
    },
    getParentRoute: () => parentRoute,
  })

  const loginRoute = createRoute({
    path: '/login',
    component: LoginScreenContainer,
    getParentRoute: () => parentRoute,
  })

  const profileRoute = createRoute({
    path: '/profile',
    component: ProfileScreenContainer,
    getParentRoute: () => parentRoute,
  })

  const aboutRoute = createRoute({
    path: '/about',
    component: AboutScreenContainer,
    getParentRoute: () => parentRoute,
  })

  const settingsRoute = createRoute({
    path: '/settings',
    component: SettingsScreenContainer,
    getParentRoute: () => parentRoute,
  })

  const helpRoute = createRoute({
    path: '/help',
    component: HelpScreenContainer,
    getParentRoute: () => parentRoute,
  })

  const helpCalloutStatusChangesRoute = createRoute({
    path: '/help/callout-status-changes',
    component: HelpCalloutStatusChangesScreenContainer,
    getParentRoute: () => parentRoute,
  })

  const jobsRoute = createRoute({
    path: '/jobs',
    getParentRoute: () => parentRoute,
    beforeLoad: () => {
      throw redirect({ to: '/services' })
    },
  })

  const kioskTabletRoute = createRoute({
    path: '/kiosk',
    component: KioskTabletScreenContainer,
    getParentRoute: () => parentRoute,
  })

  const kioskControlKioskRoute = createRoute({
    path: '/kiosk-control/$name',
    getParentRoute: () => parentRoute,
    beforeLoad: () => {
      throw redirect({ to: '/kiosk-control/check-in-out' })
    },
  })

  const kioskControlCheckInOutRoute = createRoute({
    path: '/kiosk-control/check-in-out',
    component: KioskScreenContainer,
    getParentRoute: () => parentRoute,
  })

  const kioskControlDevicesRoute = createRoute({
    path: '/kiosk-control/$name/devices',
    getParentRoute: () => parentRoute,
    beforeLoad: () => {
      throw redirect({ to: '/kiosk-control/devices' })
    },
  })

  const kioskControlDevicesPageRoute = createRoute({
    path: '/kiosk-control/devices',
    component: KioskScreenContainer,
    getParentRoute: () => parentRoute,
  })

  const kioskControlSettingsRoute = createRoute({
    path: '/kiosk-control/$name/settings',
    getParentRoute: () => parentRoute,
    beforeLoad: () => {
      throw redirect({ to: '/kiosk-control/check-in-out' })
    },
  })

  const kioskControlRoute = createRoute({
    path: '/kiosk-control',
    getParentRoute: () => parentRoute,
    beforeLoad: () => {
      throw redirect({ to: '/kiosk-control/check-in-out' })
    },
  })

  const kioskPairRoute = createRoute({
    path: '/kiosk-control/pair',
    component: KioskPairingApproveScreen,
    getParentRoute: () => parentRoute,
  })

  const kioskTypoRoute = createRoute({
    path: '/kisok',
    getParentRoute: () => parentRoute,
    beforeLoad: () => {
      throw redirect({ to: '/kiosk' })
    },
  })

  const jobDetailsRoute = createRoute({
    path: '/jobs/$id',
    component: JobDetailsScreenContainer,
    getParentRoute: () => parentRoute,
  })

  const jobLogWorkRoute = createRoute({
    path: '/jobs/$id/logs',
    component: JobLogWorkScreenContainer,
    getParentRoute: () => parentRoute,
  })

  const jobInvoiceRoute = createRoute({
    path: '/jobs/$id/invoice',
    component: JobInvoicePrepScreenContainer,
    getParentRoute: () => parentRoute,
  })

  return parentRoute.addChildren([
    indexRoute,
    bookingsRoute,
    customersRoute,
    customerDetailsRoute,
    customerServicesRoute,
    customerNotesRoute,
    customerHistoryRoute,
    customerDocumentsRoute,
    followUpsRoute,
    servicesRoute,
    archivedRoute,
    calloutsRoute,
    sessionsRoute,
    legacySessionsRoute,
    sessionDetailsRoute,
    legacySessionDetailsRoute,
    calloutDetailsRoute,
    calloutLogWorkRoute,
    calloutInvoiceRoute,
    sessionLogWorkRoute,
    legacySessionLogWorkRoute,
    sessionInvoiceRoute,
    legacySessionInvoiceRoute,
    remindersRoute,
    remindersArchivedRoute,
    remindersMessagesRoute,
    remindersConfigureRoute,
    reminderDetailRoute,
    whatsappRoute,
    loginRoute,
    profileRoute,
    settingsRoute,
    aboutRoute,
    helpRoute,
    helpCalloutStatusChangesRoute,
    jobsRoute,
    kioskTabletRoute,
    kioskControlKioskRoute,
    kioskControlCheckInOutRoute,
    kioskControlDevicesRoute,
    kioskControlDevicesPageRoute,
    kioskControlSettingsRoute,
    kioskControlRoute,
    kioskPairRoute,
    kioskTypoRoute,
    jobDetailsRoute,
    jobLogWorkRoute,
    jobInvoiceRoute,
  ])
}
